import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';
import { toast } from 'sonner';
import { Receipt, UploadSimple, Check, ChatCircleDots, CalendarBlank, FileArrowDown } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { countsLabel, fmtMonth } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  pendente: { label: 'Pendente', color: 'bg-brand-bg text-brand-muted' },
  em_validacao: { label: 'Aguarda a tua validação', color: 'bg-amber-50 text-amber-700' },
  corrigido: { label: 'Aguarda admin', color: 'bg-orange-50 text-orange-700' },
  aprovado: { label: 'Aguarda o teu recibo', color: 'bg-blue-50 text-blue-700' },
  recibo_enviado: { label: 'Recibo enviado', color: 'bg-indigo-50 text-indigo-700' },
  pago: { label: 'Pago', color: 'bg-green-50 text-green-700' },
};

const ROW_STATUS = {
  pago: { label: 'Pago', color: 'bg-green-50 text-green-700' },
  nao_pago: { label: 'Não pago', color: 'bg-red-50 text-red-600' },
  prestacao_paga: { label: 'Prestação de programa', color: 'bg-green-50 text-green-700' },
  incluido_pacote: { label: 'Incluída no programa', color: 'bg-brand-bg text-brand-muted' },
  aguarda_reembolso: { label: 'Aguarda reembolso', color: 'bg-amber-50 text-amber-700' },
  reembolso_cheque: { label: 'Reembolsado', color: 'bg-green-50 text-green-700' },
  reembolso_cheque_outro_mes: { label: 'Reembolso (cheque)', color: 'bg-green-50 text-green-700' },
  cheque_pendente: { label: 'Cheque por associar', color: 'bg-orange-50 text-orange-600' },
};

function fmt(n) { const num = Number(n) || 0; return num.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function ConsultationsTable(items, testId) {
  return (
    <table className="w-full min-w-[520px] text-xs">
      <thead className="bg-brand-bg/40 text-brand-muted">
        <tr><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Paciente</th><th className="text-center px-3 py-2">Estado</th><th className="text-right px-3 py-2">Bruto</th><th className="text-right px-3 py-2">Taxa</th></tr>
      </thead>
      <tbody>
        {items.map((c, i) => (
          <tr key={c.consultation_id || i} className="border-t border-brand-border/40" data-testid={`${testId}-row-${c.consultation_id || i}`}>
            <td className="px-3 py-2">{fmtDatePt(c.date)}</td>
            <td className="px-3 py-2 truncate">{c.patient_name || '-'}</td>
            <td className="px-3 py-2 text-center">
              <span className={`text-[9px] px-2 py-0.5 rounded-full ${ROW_STATUS[c.status]?.color || (c.is_paid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600')}`} data-testid={`${testId}-status-${c.consultation_id || i}`}>{ROW_STATUS[c.status]?.label || (c.is_paid ? 'Pago' : 'Não pago')}</span>
            </td>
            <td className="px-3 py-2 text-right">{fmt(c.gross_amount)} €</td>
            <td className="px-3 py-2 text-right text-amber-600">{fmt(c.stripe_fee)} €</td>
          </tr>
        ))}
        {!items.length && <tr><td colSpan="5" className="text-center py-4 text-brand-muted">Sem consultas neste mês.</td></tr>}
      </tbody>
    </table>
  );
}

function TeamMemberCard({ snap }) {
  const [open, setOpen] = useState(false);
  const items = snap.consultations || [];
  return (
    <div className="bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid={`team-member-${snap.nutri_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 hover:bg-brand-bg/20 text-left">
        <div>
          <p className="text-sm font-medium text-brand-text">{snap.nutri_name}</p>
          <p className="text-[11px] text-brand-muted">{countsLabel(snap)} · {snap.payments_count} pagas · {fmt(snap.gross_total)} € bruto</p>
        </div>
        <span className="text-sm font-semibold text-brand-green">{fmt(snap.payable_amount)} €</span>
      </button>
      {open && <div className="border-t border-brand-border/50">{ConsultationsTable(items, `team-${snap.nutri_id}`)}</div>}
    </div>
  );
}

export default function MeusPagamentosPage() {
  useRequireAdmin();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ snapshot: { consultations: [] }, current: null, history: [] });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/team/payments/mine?year_month=${month}`);
      setData(data || { snapshot: { consultations: [] }, current: null, history: [] });
    } catch {}
  }, [month]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const current = data.current;
  const snap = data.snapshot || { consultations: [] };
  const items = snap.consultations || [];
  const teamSnapshots = data.team_snapshots || null;

  const submitNote = async () => {
    if (!current?.id || !note.trim()) { toast.error('Escreve uma nota'); return; }
    setBusy(true);
    try { await axios.post(`${API}/api/team/payments/${current.id}/note`, { note }); toast.success('Nota enviada à admin'); setNote(''); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    if (!current?.id) return;
    setBusy(true);
    try { await axios.post(`${API}/api/team/payments/${current.id}/approve`); toast.success('Aprovado. Já podes carregar o recibo.'); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };
  const uploadReceipt = async (file) => {
    if (!current?.id || !file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) { toast.error('Tem de ser PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Máximo 10MB'); return; }
    const form = new FormData(); form.append('file', file);
    setBusy(true);
    try { await axios.post(`${API}/api/team/payments/${current.id}/receipt`, form, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Recibo enviado'); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setBusy(false); }
  };

  const downloadReceipt = () => {
    if (!current?.id) return;
    window.open(`${API}/api/team/payments/${current.id}/receipt`, '_blank');
  };

  return (
    <div className="fade-in" data-testid="meus-pagamentos-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Os meus pagamentos</h2>
          <p className="text-xs text-brand-muted mt-0.5">Resumo do mês, nota de correção e recibo verde</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} className="text-brand-muted" />
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-xl border-brand-border w-44 text-sm" data-testid="mine-month-picker" />
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="mine-summary">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <p className="text-sm font-medium text-brand-text">Resumo de {fmtMonth(snap.year_month || month)}</p>
          {current && <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS[current.status]?.color || 'bg-brand-bg text-brand-muted'}`}>{STATUS[current.status]?.label || current.status}</span>}
          {!current && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-bg text-brand-muted">Mês ainda não fechado</span>}
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-brand-bg/40 rounded-xl p-3"><p className="text-[10px] uppercase text-brand-placeholder">Consultas e prestações</p><p className="text-sm font-medium text-brand-text">{countsLabel(snap)}</p></div>
          <div className="bg-brand-bg/40 rounded-xl p-3"><p className="text-[10px] uppercase text-brand-placeholder">Bruto</p><p className="text-sm font-medium text-brand-text">{fmt(snap.gross_total)} €</p></div>
          <div className="bg-brand-green/10 rounded-xl p-3"><p className="text-[10px] uppercase text-brand-placeholder">A receber ({snap.percentage || 0}%)</p><p className="text-sm font-semibold text-brand-green">{fmt(snap.payable_amount)} €</p></div>
        </div>
        {current?.admin_reply && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-2.5">
            <p className="text-[10px] uppercase text-amber-700 mb-1">Resposta da admin</p>
            <p className="text-xs text-brand-text italic">“{current.admin_reply}”</p>
          </div>
        )}
      </div>

      {/* Consultations table */}
      <div className="bg-white border border-brand-border rounded-2xl overflow-x-auto mb-4" data-testid="mine-consultations">
        {ConsultationsTable(items, 'mine')}
      </div>

      {/* Equipa (visível com o toggle "Ver toda a equipa" ligado no menu lateral) */}
      {teamSnapshots && (
        <div className="mb-4" data-testid="team-payments-section">
          <h3 className="font-heading text-sm text-brand-text mb-2">Equipa · {month}</h3>
          <div className="space-y-2">
            {teamSnapshots.map(s => <TeamMemberCard key={s.nutri_id} snap={s} />)}
          </div>
        </div>
      )}

      {/* Actions */}
      {current && current.status === 'em_validacao' && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 space-y-3" data-testid="mine-action-validate">
          <p className="text-sm text-amber-800 font-medium">A admin enviou-te o resumo para validação.</p>
          <div>
            <Label className="text-[11px] uppercase text-brand-placeholder block mb-1">Tens correções? Deixa uma nota:</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Ex: a taxa Stripe da consulta de 08/05 está em falta…" className="rounded-xl border-brand-border text-sm resize-none" data-testid="mine-note" />
          </div>
          <div className="flex gap-2">
            <button onClick={submitNote} disabled={busy || !note.trim()} className="border border-brand-border text-brand-muted rounded-full px-4 py-2 text-xs flex items-center gap-1 disabled:opacity-50" data-testid="mine-send-note"><ChatCircleDots size={14} /> Enviar nota</button>
            <button onClick={approve} disabled={busy} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs flex items-center gap-1 disabled:opacity-50 ml-auto" data-testid="mine-approve"><Check size={14} /> Está tudo certo, aprovo</button>
          </div>
        </div>
      )}

      {current && current.status === 'aprovado' && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-4" data-testid="mine-action-upload">
          <p className="text-sm text-blue-800 font-medium mb-2">Já aprovaste. Falta enviares o recibo verde (PDF).</p>
          <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={e => uploadReceipt(e.target.files?.[0])} data-testid="mine-receipt-file" />
          <button onClick={() => fileInputRef.current?.click()} disabled={busy} className="bg-brand-green text-white rounded-full px-4 py-2 text-xs flex items-center gap-1" data-testid="mine-upload-btn"><UploadSimple size={14} /> Carregar recibo PDF</button>
        </div>
      )}

      {current && (current.status === 'recibo_enviado' || current.status === 'pago') && current.receipt_path && (
        <div className="bg-brand-bg/40 border border-brand-border rounded-2xl p-3 mb-4 flex items-center justify-between" data-testid="mine-receipt-uploaded">
          <p className="text-xs text-brand-muted flex items-center gap-2"><Receipt size={14} /> Recibo enviado{current.status === 'pago' ? ' · pagamento concluído' : ' · aguarda confirmação da admin'}</p>
          <button onClick={downloadReceipt} className="text-xs text-brand-green hover:underline flex items-center gap-1" data-testid="mine-download-receipt"><FileArrowDown size={12} /> Ver recibo</button>
        </div>
      )}

      {/* History */}
      <div>
        <h3 className="font-heading text-sm text-brand-text mb-2">Histórico</h3>
        <div className="space-y-2" data-testid="mine-history">
          {(data.history || []).length === 0 && <p className="text-xs text-brand-placeholder italic">Sem pagamentos anteriores.</p>}
          {(data.history || []).map(h => (
            <div key={h.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center gap-3" data-testid={`mine-history-${h.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-text">{fmtMonth(h.year_month)}</p>
                <p className="text-[11px] text-brand-muted">{countsLabel(h)} · {fmt(h.payable_amount)} €</p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS[h.status]?.color || 'bg-brand-bg text-brand-muted'}`}>{STATUS[h.status]?.label || h.status}</span>
              {h.receipt_path && (
                <button onClick={() => window.open(`${API}/api/team/payments/${h.id}/receipt`, '_blank')} className="text-xs text-brand-green hover:underline flex items-center gap-1" data-testid={`mine-history-receipt-${h.id}`}><FileArrowDown size={12} /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
